/**
 * Wrapper object to Web Audio API.
 * Helper for recroding, media and stream manipulation
 */
const BUFF_SIZE = 2048
const NUM_CHAN = 2
const MIME_TYPE = 'audio/webm; codecs=opus'
const MIME_TYPE_ALTERNATIVE = 'audio/mp4; codecs=mp4a.40.2'
const SAMPLE_RATE = 44100
const VOLUME = 1

class RecProc {
  // MediaStream
  constructor (stream, onStart, onStop, isSafari = false) {
    // Safari does not support audio/webm, only audio/mp4 (for now, maybe?)
    this.mimeType = isSafari ? MIME_TYPE_ALTERNATIVE : MIME_TYPE
    // set media recorder
    this._initMediaRecorder(stream, onStart, onStop, isSafari)

    // set context for export
    this._initExporter(stream)
  }

  start () {
    this.rec.start(1000) // chunks of 1s
  }

  stop () {
    if (this.rec.state !== 'recording') {
      return
    }
    this.rec.stop()
    // release media
    this.stream.getTracks().forEach(track => {
      track.stop()
      this.stream.removeTrack(track)
    })

    this.src.disconnect()
    this.prc.disconnect()

    this.stream = null
  }

  playerSrcURL () {
    const blob = new Blob(this.recChunks, { type: this.mimeType })
    return URL.createObjectURL(blob)
  }

  toWave () {
    const data = this._interLeaveData()
    const len = data.length * 2
    const buffer = new ArrayBuffer(44 + len)
    const view = new DataView(buffer)

    view.setUint32(0, 1380533830, false) // RIFF identifier 'RIFF'
    view.setUint32(4, 36 + len, true)
    view.setUint32(8, 1463899717, false) // RIFF type 'WAVE'
    view.setUint32(12, 1718449184, false) // format chunk identifier 'fmt '
    view.setUint32(16, 16, true) // format chunk length
    view.setUint16(20, 0x01, true) // sample format (pcm)
    view.setUint16(22, NUM_CHAN, true)
    view.setUint32(24, SAMPLE_RATE, true) // sample rate
    view.setUint32(28, SAMPLE_RATE * 4, true) // byte rate (sample rate * block align)
    view.setUint16(32, NUM_CHAN * 2, true) // block align (channel count * bytes per sample)
    view.setUint16(34, 16, true) // bits per sample
    view.setUint32(36, 1684108385, false) // data chunk identifier 'data'
    view.setUint32(40, len, true) // data chunk length

    // write data
    for (let i = 0, j = 44; i < data.length; i++, j += 2) {
      view.setInt16(j, data[i] * (0x7FFF * VOLUME), true)
    }

    return new Blob([view], { type: 'audio/wav' })
  }

  _interLeaveData () {
    const merge = function (buf, len) {
      const data = new Float32Array(len)
      let offset = 0
      for (let i = 0; i < buf.length; i++) {
        const b = buf[i]
        data.set(b, offset)
        offset += b.length
      }
      return data
    }

    const lChan = merge(this.lChan, this.recLen)
    const rChan = merge(this.rChan, this.recLen)

    const len = lChan.length + rChan.length
    const buffer = new Float32Array(len)
    for (let i = 0, j = 0; i < len; j++) {
      buffer[i++] = lChan[j]
      buffer[i++] = rChan[j]
    }
    return buffer
  }

  _initMediaRecorder (stream, onStart, onStop) {
    this.recChunks = []

    this.stream = stream
    this.rec = new MediaRecorder(stream, { mimeType: this.mimeType })
    this.rec.ondataavailable = e => {
      if (e.data && e.data.size) { // <- Safari fix
        this.recChunks.push(e.data)
      }
    }
    this.rec.onstart = onStart
    this.rec.onstop = onStop
  }

  _initExporter (stream) {
    this.lChan = []
    this.rChan = []
    this.recLen = 0

    this.ctx = new AudioContext()
    this.src = this.ctx.createMediaStreamSource(stream)
    this.prc = this.ctx.createScriptProcessor(BUFF_SIZE, NUM_CHAN, NUM_CHAN)
    this.src.connect(this.prc)
    this.prc.connect(this.ctx.destination)
    this.prc.onaudioprocess = (e) => {
      this.lChan.push(new Float32Array(e.inputBuffer.getChannelData(0)))
      this.rChan.push(new Float32Array(e.inputBuffer.getChannelData(1)))
      this.recLen += BUFF_SIZE
    }
  }
}

export default function (stream, onStart, onStop, isSafari = false) {
  return new RecProc(stream, onStart, onStop, isSafari)
}
